import { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus,
  Search,
  Pencil,
  ChevronDown,
  ChevronUp,
  Loader2,
  AlertCircle,
  Users,
} from 'lucide-react'

import { Button } from '@payroll/components/ui/button'
import { Input } from '@payroll/components/ui/input'
import { Card, CardContent } from '@payroll/components/ui/card'
import { Badge } from '@payroll/components/ui/badge'
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@payroll/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@payroll/components/ui/dialog'
import { Select } from '@payroll/components/ui/select'

import { api } from '@payroll/utils/api'
import { formatCurrency, formatDate, formatNationalId, formatMonthEn } from '@payroll/utils/format'
import { validateThaiNationalId } from '@payroll/utils/thai-id-validator'
import type {
  Employee,
  EmployeeFormData,
  PayrollEntry,
} from '@payroll/types'
import {
  THAI_TITLES,
  COMPANY_TAX_IDS,
  DEPARTMENTS,
} from '@payroll/types'

// ---------------------------------------------------------------------------
// Initial form state
// ---------------------------------------------------------------------------

const EMPTY_FORM: EmployeeFormData = {
  employee_code: '',
  title_th: 'นาย',
  first_name_th: '',
  last_name_th: '',
  first_name_en: '',
  last_name_en: '',
  nickname: '',
  national_id: '',
  email: '',
  department: DEPARTMENTS[0],
  position: '',
  bank_name: '',
  bank_account: '',
  employment_type: 'monthly',
  start_date: '',
  end_date: '',
  base_salary: 0,
  ss_exempt: false,
  ss_exempt_reason: '',
  company_tax_id: COMPANY_TAX_IDS[0].id,
  payment_method: 'bank_transfer',
  is_active: true,
}

// ---------------------------------------------------------------------------
// Payroll history row (inline expand)
// ---------------------------------------------------------------------------

function PayrollHistoryPanel({ employeeId }: { employeeId: string }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['employee-payroll-history', employeeId],
    queryFn: () =>
      api.get<PayrollEntry[]>(`/employees/${employeeId}/payroll-history`),
  })

  if (isLoading) {
    return (
      <tr>
        <td colSpan={8} className="px-6 py-4 bg-gray-50">
          <div className="flex items-center gap-2 text-gray-500 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading payroll history...
          </div>
        </td>
      </tr>
    )
  }

  if (isError || !data?.data) {
    return (
      <tr>
        <td colSpan={8} className="px-6 py-4 bg-gray-50">
          <div className="flex items-center gap-2 text-red-600 text-sm">
            <AlertCircle className="h-4 w-4" />
            Failed to load payroll history.
          </div>
        </td>
      </tr>
    )
  }

  const entries = data.data

  if (entries.length === 0) {
    return (
      <tr>
        <td colSpan={8} className="px-6 py-8 bg-gray-50 text-center text-sm text-gray-500">
          No payroll history found.
        </td>
      </tr>
    )
  }

  return (
    <tr>
      <td colSpan={8} className="p-0">
        <div className="bg-gray-50 border-t border-gray-200 px-6 py-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">
            Payroll History
          </h4>
          <div className="overflow-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-200 text-gray-500">
                  <th className="text-left py-2 pr-4 font-medium">Period</th>
                  <th className="text-right py-2 pr-4 font-medium">Base Salary</th>
                  <th className="text-right py-2 pr-4 font-medium">Total Income</th>
                  <th className="text-right py-2 pr-4 font-medium">Tax</th>
                  <th className="text-right py-2 pr-4 font-medium">SS</th>
                  <th className="text-right py-2 pr-4 font-medium">Total Deduction</th>
                  <th className="text-right py-2 font-medium">Net Pay</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry: any) => (
                  <tr key={entry.id} className="border-b border-gray-100 last:border-0">
                    <td className="py-2 pr-4 text-gray-700 font-medium">
                      {entry.year && entry.month ? `${formatMonthEn(entry.month)} ${entry.year}` : formatDate(entry.created_at)}
                    </td>
                    <td className="py-2 pr-4 text-right">{formatCurrency(entry.base_salary)}</td>
                    <td className="py-2 pr-4 text-right">{formatCurrency(entry.total_income)}</td>
                    <td className="py-2 pr-4 text-right">{formatCurrency(entry.income_tax)}</td>
                    <td className="py-2 pr-4 text-right">{formatCurrency(entry.social_security)}</td>
                    <td className="py-2 pr-4 text-right">{formatCurrency(entry.total_deduction)}</td>
                    <td className="py-2 text-right font-medium">{formatCurrency(entry.net_pay)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </td>
    </tr>
  )
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function EmployeesPage() {
  const queryClient = useQueryClient()

  // ---- State ----
  const [search, setSearch] = useState('')
  const [filterDept, setFilterDept] = useState<string>('all')
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [form, setForm] = useState<EmployeeFormData>({ ...EMPTY_FORM })
  const [nationalIdError, setNationalIdError] = useState<string | null>(null)
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})

  // ---- Data fetching ----
  const {
    data: employeesRes,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['employees'],
    queryFn: () => api.get<Employee[]>('/employees'),
  })

  const employees = employeesRes?.data ?? []

  // ---- Mutations ----
  const createMutation = useMutation({
    mutationFn: (data: EmployeeFormData) =>
      api.post<Employee>('/employees', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] })
      closeDialog()
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: EmployeeFormData }) =>
      api.put<Employee>(`/employees/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] })
      closeDialog()
    },
  })

  const isSaving = createMutation.isPending || updateMutation.isPending

  // ---- Helpers ----
  const closeDialog = useCallback(() => {
    setDialogOpen(false)
    setEditingEmployee(null)
    setForm({ ...EMPTY_FORM })
    setNationalIdError(null)
    setFormErrors({})
    createMutation.reset()
    updateMutation.reset()
  }, [createMutation, updateMutation])

  const openCreate = useCallback(() => {
    setEditingEmployee(null)
    setForm({ ...EMPTY_FORM })
    setNationalIdError(null)
    setFormErrors({})
    setDialogOpen(true)
  }, [])

  const openEdit = useCallback((emp: Employee) => {
    setEditingEmployee(emp)
    setForm({
      employee_code: emp.employee_code,
      title_th: emp.title_th,
      first_name_th: emp.first_name_th,
      last_name_th: emp.last_name_th,
      first_name_en: emp.first_name_en ?? '',
      last_name_en: emp.last_name_en ?? '',
      nickname: emp.nickname ?? '',
      national_id: emp.national_id,
      email: emp.email,
      department: emp.department,
      position: emp.position ?? '',
      bank_name: emp.bank_name ?? '',
      bank_account: emp.bank_account ?? '',
      employment_type: emp.employment_type,
      start_date: emp.start_date,
      end_date: emp.end_date ?? '',
      base_salary: emp.base_salary,
      ss_exempt: emp.ss_exempt,
      ss_exempt_reason: emp.ss_exempt_reason ?? '',
      company_tax_id: emp.company_tax_id,
      payment_method: emp.payment_method,
      // D1 returns 0/1, not a real boolean
      is_active: Boolean(emp.is_active),
    })
    setNationalIdError(null)
    setFormErrors({})
    setDialogOpen(true)
  }, [])

  const setField = useCallback(
    <K extends keyof EmployeeFormData>(key: K, value: EmployeeFormData[K]) => {
      setForm((prev) => ({ ...prev, [key]: value }))
      // Clear error for this field when user edits it
      setFormErrors((prev) => {
        if (prev[key]) {
          const next = { ...prev }
          delete next[key]
          return next
        }
        return prev
      })
    },
    [],
  )

  const validateNationalId = useCallback(() => {
    if (!form.national_id) {
      setNationalIdError(null)
      return
    }
    const result = validateThaiNationalId(form.national_id)
    setNationalIdError(result.valid ? null : (result.error ?? 'Invalid'))
  }, [form.national_id])

  const validate = useCallback((): boolean => {
    const errors: Record<string, string> = {}

    if (!form.employee_code.trim()) errors.employee_code = 'Required'
    if (!form.first_name_th.trim()) errors.first_name_th = 'Required'
    if (!form.last_name_th.trim()) errors.last_name_th = 'Required'
    if (!form.national_id.trim()) {
      errors.national_id = 'Required'
    }
    if (!form.email.trim()) errors.email = 'Required'
    if (!form.department.trim()) errors.department = 'Required'
    if (!form.start_date) errors.start_date = 'Required'
    if (!form.base_salary || form.base_salary <= 0) errors.base_salary = 'Required'
    // company_tax_id is fixed — not per employee
    if (form.ss_exempt && !form.ss_exempt_reason?.trim()) {
      errors.ss_exempt_reason = 'Reason required when exempt'
    }
    if (form.end_date && form.start_date && form.end_date < form.start_date) {
      errors.end_date = 'End date cannot be before start date'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }, [form])

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      if (!validate()) return

      if (editingEmployee) {
        // Send null (not '') so clearing the end date actually clears it
        const data = { ...form, end_date: form.end_date || null }
        updateMutation.mutate({ id: editingEmployee.id, data })
      } else {
        createMutation.mutate(form)
      }
    },
    [validate, editingEmployee, form, createMutation, updateMutation],
  )

  // ---- Filtering ----
  const filtered = employees.filter((emp) => {
    // Search
    if (search) {
      const q = search.toLowerCase()
      const fullNameTh = `${emp.title_th}${emp.first_name_th} ${emp.last_name_th}`.toLowerCase()
      const fullNameEn = `${emp.first_name_en ?? ''} ${emp.last_name_en ?? ''}`.toLowerCase()
      const matched =
        emp.employee_code.toLowerCase().includes(q) ||
        fullNameTh.includes(q) ||
        fullNameEn.includes(q) ||
        emp.email.toLowerCase().includes(q) ||
        emp.national_id.includes(q)
      if (!matched) return false
    }
    // Department
    if (filterDept !== 'all' && emp.department !== filterDept) return false
    // Status
    if (filterStatus === 'active' && !emp.is_active) return false
    if (filterStatus === 'inactive' && emp.is_active) return false
    return true
  })

  // ---- Render helpers ----
  const mutationError = createMutation.error ?? updateMutation.error

  const fieldError = (key: string) =>
    formErrors[key] ? (
      <span className="text-xs text-red-600 mt-0.5">{formErrors[key]}</span>
    ) : null

  const requiredMark = <span className="text-red-500 ml-0.5">*</span>

  // ---- JSX ----
  return (
    <div className="space-y-6">
      {/* ================================================================ */}
      {/* Top bar                                                          */}
      {/* ================================================================ */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Users className="h-6 w-6 text-[#1a237e]" />
          <h1 className="text-2xl font-bold text-gray-900">
            Employees
          </h1>
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Add Employee
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by name, code, email, or ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            {/* Department filter */}
            <Select
              value={filterDept}
              onChange={(e) => setFilterDept(e.target.value)}
              className="w-full sm:w-48"
            >
              <option value="all">All Departments</option>
              {DEPARTMENTS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </Select>
            {/* Status filter */}
            <Select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full sm:w-40"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* ================================================================ */}
      {/* Employee table                                                   */}
      {/* ================================================================ */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-16 text-gray-500 gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading employees...
            </div>
          ) : isError ? (
            <div className="flex items-center justify-center py-16 text-red-600 gap-2">
              <AlertCircle className="h-5 w-5" />
              Failed to load employees.
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <Users className="h-10 w-10 mb-2" />
              <p className="text-sm">
                {employees.length === 0
                  ? 'No employees yet. Click "Add Employee" to get started.'
                  : 'No employees match your filters.'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead>Code</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Position</TableHead>
                  <TableHead className="text-right">Salary</TableHead>
                  <TableHead>SS Status</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-20" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((emp) => {
                  const isExpanded = expandedId === emp.id
                  return (
                    <>
                      <TableRow
                        key={emp.id}
                        className="cursor-pointer"
                        onClick={() =>
                          setExpandedId(isExpanded ? null : emp.id)
                        }
                      >
                        <TableCell className="pr-0">
                          {isExpanded ? (
                            <ChevronUp className="h-4 w-4 text-gray-400" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-gray-400" />
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-sm font-medium">
                          {emp.employee_code}
                        </TableCell>
                        <TableCell>
                          <div>
                            <span className="font-medium text-gray-900">
                              {emp.nickname && (
                                <span className="text-primary-900">"{emp.nickname}" </span>
                              )}
                              {emp.first_name_en || emp.first_name_th}{' '}
                              {emp.last_name_en || emp.last_name_th}
                            </span>
                          </div>
                          <div className="text-xs text-gray-400">
                            {emp.title_th}{emp.first_name_th} {emp.last_name_th}
                          </div>
                        </TableCell>
                        <TableCell>{emp.department}</TableCell>
                        <TableCell>{emp.position ?? '-'}</TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {formatCurrency(emp.base_salary)}
                        </TableCell>
                        <TableCell>
                          {emp.ss_exempt && (
                            <Badge className="bg-orange-100 text-orange-800 border-orange-200 hover:bg-orange-100">
                              SS Exempt
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {emp.is_active ? (
                            <Badge className="bg-green-100 text-green-800 border-green-200 hover:bg-green-100">
                              Active
                            </Badge>
                          ) : (
                            <Badge variant="secondary">Inactive</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation()
                              openEdit(emp)
                            }}
                            title="Edit employee"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                      {isExpanded && (
                        <PayrollHistoryPanel
                          key={`history-${emp.id}`}
                          employeeId={emp.id}
                        />
                      )}
                    </>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ================================================================ */}
      {/* Add / Edit Dialog                                                */}
      {/* ================================================================ */}
      <Dialog open={dialogOpen} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingEmployee
                ? `Edit Employee - ${editingEmployee.employee_code}`
                : 'Add Employee'}
            </DialogTitle>
            <DialogDescription>
              {editingEmployee
                ? 'Update the employee details below.'
                : 'Fill in the form to register a new employee.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Mutation error */}
            {mutationError && (
              <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700 flex items-start gap-2">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{(mutationError as Error).message}</span>
              </div>
            )}

            {/* ---------- Row 1: Code & Title ---------- */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">
                  Employee Code {requiredMark}
                </label>
                <Input
                  placeholder="e.g. MB001"
                  value={form.employee_code}
                  onChange={(e) => setField('employee_code', e.target.value)}
                />
                {fieldError('employee_code')}
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">
                  Title (Thai) {requiredMark}
                </label>
                <Select
                  value={form.title_th}
                  onChange={(e) => setField('title_th', e.target.value)}
                >
                  {THAI_TITLES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            {/* ---------- Row 2: Thai names ---------- */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">
                  First Name (Thai) {requiredMark}
                </label>
                <Input
                  placeholder="First name in Thai"
                  value={form.first_name_th}
                  onChange={(e) => setField('first_name_th', e.target.value)}
                />
                {fieldError('first_name_th')}
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">
                  Last Name (Thai) {requiredMark}
                </label>
                <Input
                  placeholder="Last name in Thai"
                  value={form.last_name_th}
                  onChange={(e) => setField('last_name_th', e.target.value)}
                />
                {fieldError('last_name_th')}
              </div>
            </div>

            {/* ---------- Row 3: English names ---------- */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">
                  First Name (English)
                </label>
                <Input
                  placeholder="First Name (English)"
                  value={form.first_name_en ?? ''}
                  onChange={(e) => setField('first_name_en', e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">
                  Last Name (English)
                </label>
                <Input
                  placeholder="Last Name (English)"
                  value={form.last_name_en ?? ''}
                  onChange={(e) => setField('last_name_en', e.target.value)}
                />
              </div>
            </div>

            {/* ---------- Nickname ---------- */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">
                  Nickname
                </label>
                <Input
                  placeholder="e.g. Kul, Oded, Mai"
                  value={form.nickname ?? ''}
                  onChange={(e) => setField('nickname', e.target.value)}
                />
                <span className="text-xs text-gray-400">Common Thai nickname for easy reference</span>
              </div>
            </div>

            {/* ---------- Row 4: National ID & Email ---------- */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">
                  National ID {requiredMark}
                </label>
                <Input
                  placeholder="13-digit National ID"
                  value={form.national_id}
                  onChange={(e) => setField('national_id', e.target.value)}
                  onBlur={validateNationalId}
                  maxLength={17}
                />
                {nationalIdError && (
                  <span className="text-xs text-red-600 mt-0.5">
                    {nationalIdError}
                  </span>
                )}
                {fieldError('national_id')}
                {form.national_id &&
                  !nationalIdError &&
                  form.national_id.replace(/[\s-]/g, '').length === 13 && (
                    <span className="text-xs text-gray-400">
                      {formatNationalId(form.national_id)}
                    </span>
                  )}
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">
                  Email {requiredMark}
                </label>
                <Input
                  type="email"
                  placeholder="email@example.com"
                  value={form.email}
                  onChange={(e) => setField('email', e.target.value)}
                />
                {fieldError('email')}
              </div>
            </div>

            {/* ---------- Row 5: Department & Position ---------- */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">
                  Department {requiredMark}
                </label>
                <Select
                  value={form.department}
                  onChange={(e) => setField('department', e.target.value)}
                >
                  {DEPARTMENTS.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </Select>
                {fieldError('department')}
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">
                  Position
                </label>
                <Input
                  placeholder="e.g. Developer, Manager"
                  value={form.position ?? ''}
                  onChange={(e) => setField('position', e.target.value)}
                />
              </div>
            </div>

            {/* ---------- Row 6: Bank ---------- */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">
                  Bank Name
                </label>
                <Input
                  placeholder="e.g. KBank, SCB, BBL"
                  value={form.bank_name ?? ''}
                  onChange={(e) => setField('bank_name', e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">
                  Bank Account
                </label>
                <Input
                  placeholder="Account number"
                  value={form.bank_account ?? ''}
                  onChange={(e) => setField('bank_account', e.target.value)}
                />
              </div>
            </div>

            {/* ---------- Row 7: Employment Type & Start Date ---------- */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">
                  Employment Type {requiredMark}
                </label>
                <Select
                  value={form.employment_type}
                  onChange={(e) =>
                    setField(
                      'employment_type',
                      e.target.value as 'monthly' | 'daily',
                    )
                  }
                >
                  <option value="monthly">Monthly</option>
                  <option value="daily">Daily</option>
                </Select>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">
                  Start Date {requiredMark}
                </label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setField('start_date', e.target.value)}
                />
                {fieldError('start_date')}
              </div>
            </div>

            {/* ---------- Row 8: Salary ---------- */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">
                  Base Salary (THB) {requiredMark}
                </label>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  placeholder="0.00"
                  value={form.base_salary || ''}
                  onChange={(e) =>
                    setField('base_salary', parseFloat(e.target.value) || 0)
                  }
                />
                {fieldError('base_salary')}
              </div>
            </div>

            {/* ---------- Row 9: Payment Method ---------- */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">
                  Payment Method {requiredMark}
                </label>
                <Select
                  value={form.payment_method}
                  onChange={(e) =>
                    setField(
                      'payment_method',
                      e.target.value as 'bank_transfer' | 'check',
                    )
                  }
                >
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="check">Check</option>
                </Select>
              </div>
            </div>

            {/* ---------- SS Exemption ---------- */}
            <div className="rounded-md border border-gray-200 p-4 space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.ss_exempt}
                  onChange={(e) => {
                    setField('ss_exempt', e.target.checked)
                    if (!e.target.checked) setField('ss_exempt_reason', '')
                  }}
                  className="h-4 w-4 rounded border-gray-300 text-[#1a237e] focus:ring-[#1a237e]"
                />
                <span className="text-sm font-medium text-gray-700">
                  Exempt from Social Security
                </span>
              </label>
              {form.ss_exempt && (
                <div className="space-y-1.5 pl-7">
                  <label className="text-sm font-medium text-gray-700">
                    Reason for Exemption {requiredMark}
                  </label>
                  <Input
                    placeholder="e.g. Owner, Over 60 years old"
                    value={form.ss_exempt_reason ?? ''}
                    onChange={(e) =>
                      setField('ss_exempt_reason', e.target.value)
                    }
                  />
                  {fieldError('ss_exempt_reason')}
                </div>
              )}
            </div>

            {/* ---------- Employment Status ---------- */}
            <div className="rounded-md border border-gray-200 p-4 space-y-3">
              {editingEmployee && (
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setField('is_active', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-[#1a237e] focus:ring-[#1a237e]"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Active employee
                  </span>
                  {!form.is_active && (
                    <span className="text-xs text-gray-500">
                      Not included in new payroll periods
                    </span>
                  )}
                </label>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-gray-700">
                    End Date (last working day)
                  </label>
                  <Input
                    type="date"
                    value={form.end_date ?? ''}
                    onChange={(e) => setField('end_date', e.target.value)}
                  />
                  {fieldError('end_date')}
                </div>
              </div>
            </div>

            {/* ---------- Footer ---------- */}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={closeDialog}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingEmployee ? 'Update Employee' : 'Create Employee'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
